import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import ListItemDot from 'features/IRWorkstation/components/Common/ListItemDot';

const priorityColorMap = {
	High: 'signal.red',
	Normal: 'signal.amber',
	Low: 'signal.green',
};

const SalesforceTaskRow = ({ task, onClick }) => {
	const priorityColor = priorityColorMap[task.priority] || 'signal.gray';

	return (
		<Box
			onClick={() => onClick(task)}
			sx={{
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'flex-start',
				gap: '12px',
				padding: '10px 15px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
				cursor: 'pointer',
				transition: 'background-color 0.2s',
				'&:hover': {
					backgroundColor: 'background.subtle',
				},
			}}
		>
			{/* Priority Dot */}
			<ListItemDot color={priorityColor} />

			{/* Main Content Container - switches to column layout on xs screens */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					gap: '4px',
					width: '100%',
				}}
			>
				{/* Column 2 - subject, priority, status */}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						sx={{
							fontSize: '12.5px',
							fontWeight: 600,
							color: 'text.primary',
							lineHeight: 1.4,
							mb: '2px',
						}}
					>
						{task.subject || '(No Subject)'}
					</Typography>

					<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
						<Box
							component='span'
							sx={{
								color: priorityColor,
								fontWeight: 700,
								fontSize: '10px',
								textTransform: 'uppercase',
							}}
						>
							{task.priority}
						</Box>
						<Typography
							component='span'
							sx={{
								fontSize: '11px',
								color: 'text.secondary',
							}}
						>
							· {task.status}
						</Typography>
					</Box>
				</Box>

				{/* Column 3 - due date and related to (moves under column 2 on xs screens) */}
				<Box
					sx={{
						minWidth: 0,
						flex: { xs: 'unset', sm: 1 },
						display: 'flex',
						flexDirection: 'column',
						alignItems: { xs: 'flex-start', sm: 'flex-end' },
						gap: '2px',
						flexWrap: 'wrap',
					}}
				>
					<Typography
						sx={{
							fontSize: '11px',
							color: 'text.tertiary',
							fontWeight: 500,
						}}
					>
						{task.due_date ? `Due ${task.due_date}` : 'No due date'}
					</Typography>

					{task.related_to && (
						<Typography
							noWrap
							sx={{
								fontSize: '10px',
								fontWeight: 600,
								color: 'primary.main',
								width: '100%',
								textAlign: { xs: 'left', sm: 'right' },
							}}
						>
							{task.related_to}
						</Typography>
					)}
				</Box>
			</Box>
		</Box>
	);
};

SalesforceTaskRow.propTypes = {
	task: PropTypes.shape({
		id: PropTypes.string.isRequired,
		subject: PropTypes.string,
		status: PropTypes.string,
		priority: PropTypes.string,
		due_date: PropTypes.string,
		comments: PropTypes.string,
		related_to: PropTypes.string,
		name: PropTypes.string,
	}).isRequired,
	onClick: PropTypes.func.isRequired,
};

export default SalesforceTaskRow;
